import type { IxdPreviewCompareSourceSpec } from "@/apis/compare/route";
import { logUserAction, type UserActionFile } from "@/apis/logs/route";
import { toast } from "sonner";

/**
 * IXD-IXD 대사 결과 탭(`/ixd-preview-compare/view`)과 여는 쪽 사이의 postMessage 핸드셰이크.
 *
 * 소스 spec(업로드 File 포함)은 URL 로 직렬화할 수 없어 새 탭이 ready 를 알리면 spec 을
 * 넘긴다(structured clone 이 File 을 그대로 복제). 대사 화면과 홈 패널이 함께 쓰고,
 * 받는 쪽(view)도 같은 메시지 이름을 여기서 가져간다.
 */
export const RESULT_TAB_READY = "ixd-preview-view-ready";
export const RESULT_TAB_SPECS = "ixd-preview-view-specs";

export interface ResultTabSpecsMessage {
  type: typeof RESULT_TAB_SPECS;
  src: IxdPreviewCompareSourceSpec;
  trg: IxdPreviewCompareSourceSpec;
}

/** 사용자 제스처 안에서 동기로 불러야 한다. `false` 면 팝업이 막혀 탭을 열지 못했다. */
const openResultTab = (
  src: IxdPreviewCompareSourceSpec,
  trg: IxdPreviewCompareSourceSpec,
): boolean => {
  const win = window.open("/ixd-preview-compare/view", "_blank");
  if (!win) return false;
  const onMessage = (event: MessageEvent) => {
    if (event.origin !== window.location.origin) return;
    if (event.source !== win) return;
    if ((event.data as { type?: string })?.type !== RESULT_TAB_READY) return;
    win.postMessage(
      { type: RESULT_TAB_SPECS, src, trg } satisfies ResultTabSpecsMessage,
      window.location.origin,
    );
    window.removeEventListener("message", onMessage);
  };
  window.addEventListener("message", onMessage);
  return true;
};

/**
 * IXD↔IXD 대사 진입점 — 결과 탭을 열고, 열렸으면 같은 라벨로 행동 로그를 남긴다.
 * 대사 화면과 홈 STEP6 패널이 이 함수만 부르게 해서 안내 문구·로그 라벨이 갈라지지 않게 한다.
 * 사용자 제스처 안에서 동기로 불러야 한다.
 *
 * WHY 여기만 `warnPopupBlocked`(같은 탭 이동 탈출구)가 아니라 에러 토스트인가:
 * 대사 입력은 업로드 File 이라 URL 에 못 싣고 opener 와의 postMessage 로만 넘어간다.
 * 지금 탭을 떠나면 넘길 입력 자체가 사라지므로 줄 수 있는 탈출구가 "팝업 허용" 뿐이다.
 * (DSD↔IXD 는 대사가 이미 끝나 comparisonId 가 있어 같은 탭 이동이 성립한다.)
 */
export const startIxdIxdCompare = (
  src: IxdPreviewCompareSourceSpec,
  trg: IxdPreviewCompareSourceSpec,
  files: UserActionFile[],
) => {
  if (!openResultTab(src, trg)) {
    toast.error("팝업이 차단되었습니다. 팝업을 허용해주세요.");
    return;
  }
  logUserAction("IXD-IXD 대사", files);
};
